import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Handshake } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import api from "../lib/api";
import ContentCard from "../components/ContentCard";

export default function Sponsors() {
  const { t, lang, translateBatch } = useLang();
  const { sponsorsEnabled, loaded } = useSettings();
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/content", { params: { category: "sponsor" } }).then(async (res) => {
      let data = res.data;
      if (lang === "it" && data.length) {
        const titles = await translateBatch(data.map((d) => d.title));
        const descs = await translateBatch(data.map((d) => d.description || ""));
        data = data.map((d, i) => ({ ...d, title: titles[i], description: descs[i] }));
      }
      if (alive) { setItems(data); setReady(true); }
    }).catch(() => alive && setReady(true));
    return () => { alive = false; };
  }, [lang, translateBatch]);

  if (loaded && !sponsorsEnabled) return <Navigate to="/" replace />;

  const partners = items.filter((i) => i.logo_url);

  return (
    <div data-testid="sponsors-page" className="min-h-screen px-6 pt-32 pb-28 md:px-10">
      <div className="mx-auto max-w-[1600px]">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3">
            <Handshake size={26} className="text-cyan" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">— Partners</p>
          </div>
          <h1 className="mt-4 font-head text-5xl font-black uppercase tracking-tighter text-white md:text-7xl">
            {t("supported_by")}
          </h1>
          <p className="mt-4 max-w-xl font-body text-slate-400">{t("supported_sub")}</p>
        </motion.div>

        {partners.length > 0 && (
          <div className="mt-12">
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">{t("partners")}</p>
            <div className="grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-5">
              {partners.map((p, i) => {
                const Wrapper = p.link_url ? "a" : "div";
                const wprops = p.link_url ? { href: p.link_url, target: "_blank", rel: "noreferrer" } : {};
                return (
                  <motion.div key={p.id} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: (i % 5) * 0.05 }} className={p.featured ? "relative ring-1 ring-inset ring-cyan/60" : "relative"}>
                    {p.featured && (
                      <span className="absolute left-2 top-2 z-10 bg-cyan px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-widest text-ink-900">★ {t("featured_label")}</span>
                    )}
                    <Wrapper data-testid={`partner-logo-${p.id}`} {...wprops} className="group flex h-32 items-center justify-center bg-ink-800 p-6 transition-colors hover:bg-ink-700" title={p.title}>
                      <img src={p.logo_url} alt={p.title} className="max-h-16 max-w-full object-contain opacity-70 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0" />
                    </Wrapper>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-14">
          {ready && items.length === 0 ? (
            <p className="font-body text-slate-500">{t("empty_section")}</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((item, i) => (
                <ContentCard key={item.id} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
